import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../../components/SeoHead'
import { COMPANY, IMAGES } from '../../data/siteData'
import { PAGE_META } from '../../data/metaData'
import { getOptimizedImageUrl } from '../../lib/imageHelper'
import ContactForm from '../../components/ContactForm'
import GallerySection from '../../components/GallerySection'

export default function GravearbejdePage() {
  const metaTitle = PAGE_META.gravearbejde.title
  const metaDescription = PAGE_META.gravearbejde.description

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        canonicalPath="/gravearbejde"
      />

      {/* Hero */}
      <section className="relative bg-stone-950 text-white overflow-hidden py-16 sm:py-24 border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/385186cd-968f-4839-8612-1c0b142a077c/gravearbejde-minigraver-fliseterrasse-have.jpeg", { width: 1800, quality: 80 })}
            alt="JT Knudsen i arbejde med minigraver til haveanlæg og fliseterrasse"
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs sm:text-sm font-semibold mb-4">
              <i className="fa-solid fa-trowel-bricks" aria-hidden="true"></i>
              <span>Maskingravning med minigraver</span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight drop-shadow-md">
              Gravearbejde med moderne og skånsomt maskineri
            </h1>

            <p className="mt-5 text-base sm:text-xl text-stone-200 leading-relaxed max-w-2xl drop-shadow-sm">
              <Link to="/" className="text-white font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-400 transition-colors">JT Knudsen</Link> tilbyder fleksibelt og professionelt gravearbejde til både private boligejere og mindre erhverv. Vi klarer alt fra mindre udgravninger og haveplanering til rendegravning og jordflytning i Østjylland og på Djursland.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-lg bg-[#015205] hover:bg-[#026b07] text-white font-bold text-base shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Få et tilbud på gravearbejde</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </a>

              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-4 rounded-lg bg-stone-900 hover:bg-stone-800 text-stone-100 font-bold text-base border border-stone-700 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
              Alsidige graveopgaver
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-1">
              Hvad kan vi hjælpe med inden for gravearbejde?
            </h2>
            <p className="mt-3 text-stone-700 text-base sm:text-lg leading-relaxed">
              Vores minigraver er udstyret med gummibånd, der skåner underlaget, og forskellige skovlstørrelser, så vi kan arbejde præcist selv på trange arealer, hvor større maskiner ikke kan komme til.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-arrows-split-up-and-left" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Jordflytning & Planering
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Flytning og omfordeling af muld og råjord på grunden, samt grov- og finplanering af skråninger, plæner og bede.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-road" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Rendegravning & Kabler
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Præcis udgravning af smalle render til drænrør, el-kabler, vandledninger og fiberforbindelser uden unødig opgravning.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-tree" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Have- & Landskabsprojekter
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Opgravning af gamle rødder, buske, hække eller forberedelse til nye græsplæner, hegn og støttemure.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-house-chimney" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Klargøring til Byggeri
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Afrømning af muldlag og klargøring af byggefelter forud for opførelse af skure, garager, carporte og tilbygninger.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-truck" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Bortkørsel af Overskudsjord
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Hurtig og effektiv bortskaffelse eller genanvendelse af opgravet jord efter aftale med kunden.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-7 rounded-xl border border-stone-200 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center text-xl mb-4">
                <i className="fa-solid fa-handshake" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Timekørsel eller Fast Pris
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vi kan afregne efter medgået tid eller aftale en fast og gennemskuelig pris for hele opgaven.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Machine Presentation */}
      <section className="py-16 sm:py-20 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Udstyr & Maskinkraft
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Skånsom og effektiv minigraver
              </h2>
              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                At få udført gravearbejde i en eksisterende have kræver respekt for omgivelserne. Vores kompakte maskiner kan navigere gennem havelåger og snævre passager, og vi lægger ofte køreplader ud for at beskytte din græsplæne eller belægning mod trykskader.
              </p>
              <div className="space-y-2.5 pt-2 text-sm text-stone-300">
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-amber-400" aria-hidden="true"></i>
                  <span>Skånsomme gummibælter mod græs og underlag</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-amber-400" aria-hidden="true"></i>
                  <span>Kompakte mål for nem haveadgang</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-amber-400" aria-hidden="true"></i>
                  <span>Erfaren maskinfører med sans for detaljen</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-stone-800">
                <img
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/385186cd-968f-4839-8612-1c0b142a077c/udgravning-af-rende-med-minigraver-graesarmering.jpeg", { width: 800, height: 600 })}
                  alt="Kompakt minigraver udgraver præcis rende langs græsarmeringssten"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery for Gravearbejde */}
      <section className="py-16 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GallerySection
            galleryKey="gravearbejde"
            title="Billeder af gravearbejde"
            subtitle="Se minigraveren i funktion ved jordflytning, planering og udgravning"
          />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-24 bg-stone-950 text-white border-t border-stone-800" id="kontakt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Få et uforpligtende tilbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Har du brug for hjælp til maskingravning?
              </h2>
              <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
                Uanset om det drejer sig om et par timers udgravning eller et større klargøringsprojekt, er du velkommen til at kontakte Jonas Knudsen. Vi finder en god og effektiv løsning.
              </p>

              <div className="p-5 rounded-xl bg-stone-900 border border-stone-800 space-y-3 text-sm">
                <div className="font-semibold text-stone-200">Direkte kontakt:</div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-phone text-amber-400" aria-hidden="true"></i>
                  <a href={COMPANY.phoneHref} className="hover:text-amber-400 font-bold whitespace-nowrap">
                    {COMPANY.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-stone-300">
                  <i className="fa-solid fa-envelope text-amber-400" aria-hidden="true"></i>
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-amber-400 whitespace-nowrap break-keep">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm defaultService="Gravearbejde" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
